'use client';

import { OverlayViewF, OVERLAY_MOUSE_TARGET } from '@react-google-maps/api';
import { cn } from '@up2stay/up2promo-ui';
import { BadgeCheck } from 'lucide-react';

import {
  formatPinRating,
  type CompetitorPinTone,
  type CompetitorPinVariant,
} from '@/core/components/reputation/competitor-map-pins';

/** Dot beside the rating: red = rated higher than you, green = lower, ring = similar/unknown. */
export function PinToneDot({
  tone,
  className,
}: {
  tone: CompetitorPinTone;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-2 shrink-0 rounded-full',
        tone === 'threat' && 'bg-destructive',
        tone === 'ahead' && 'bg-[color:var(--ok)]',
        (tone === 'even' || tone === 'unknown') &&
          'border-[1.5px] border-muted-foreground/70',
        className,
      )}
    />
  );
}

/**
 * HTML pin (CSS line-clamp works, unlike SVG markers on iOS / Capacitor).
 *
 * Three unmistakable kinds:
 * - **own** — solid brand pill "★ 4,8 · Du"
 * - **tracked** — white chip, brand outline and a check badge, name always bold
 * - **discoverable** — small quiet chip, no outline accent (a suggestion)
 * Tone vs your rating is a dot, so it never competes with the tracked state.
 */
export function CompetitorPinOverlay({
  latitude,
  longitude,
  variant,
  tone,
  rating,
  name,
  ownLabel,
  selected,
  title,
  zIndex,
  clickable = true,
  onClick,
}: {
  latitude: number;
  longitude: number;
  variant: CompetitorPinVariant;
  tone: CompetitorPinTone;
  rating?: number;
  name: string;
  /** Text after the rating on the own pin ("Du"). */
  ownLabel: string;
  selected?: boolean;
  title: string;
  zIndex: number;
  clickable?: boolean;
  onClick?: () => void;
}) {
  const isOwn = variant === 'own';
  const isTracked = variant === 'tracked';
  const ratingText = `★ ${formatPinRating(rating)}`;

  return (
    <OverlayViewF
      position={{ lat: latitude, lng: longitude }}
      mapPaneName={OVERLAY_MOUSE_TARGET}
    >
      <div
        className="absolute"
        style={{ transform: 'translate(-50%, 0)', zIndex }}
      >
        <div
          className="flex w-max flex-col items-center"
          style={{ transform: 'translateY(-100%)' }}
        >
          <button
            type="button"
            title={title}
            disabled={!clickable}
            className={cn(
              'flex w-max flex-col items-center',
              clickable
                ? 'cursor-pointer'
                : 'pointer-events-none cursor-default',
            )}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClick?.();
            }}
          >
            <span
              className={cn(
                'inline-flex w-max shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full tabular-nums leading-none',
                isOwn &&
                  'h-[30px] bg-[image:var(--brand-grad)] px-3 text-[12.5px] font-bold text-white shadow-[var(--shadow-brand-mark)]',
                isTracked &&
                  'h-7 border-2 border-primary bg-card pl-1.5 pr-2.5 text-[12px] font-bold text-foreground shadow-md',
                !isOwn &&
                  !isTracked &&
                  'h-6 border border-border bg-card/95 px-2 text-[11.5px] font-semibold text-muted-foreground shadow-sm',
                selected &&
                  !isOwn &&
                  'ring-2 ring-primary ring-offset-2 ring-offset-card',
                selected &&
                  !isOwn &&
                  !isTracked &&
                  'border-primary text-foreground',
              )}
            >
              {isTracked ? (
                <BadgeCheck className="size-4 text-primary" aria-hidden />
              ) : null}
              {!isOwn ? <PinToneDot tone={tone} /> : null}
              {ratingText}
              {isOwn ? <span>· {ownLabel}</span> : null}
            </span>
            <span
              aria-hidden
              className={cn(
                'h-0 w-0 border-x-[5px] border-x-transparent border-t-[7px]',
                isOwn || isTracked ? 'border-t-primary' : 'border-t-border',
              )}
            />
          </button>
        </div>
        {name.trim() ? (
          <p
            className={cn(
              'pointer-events-none absolute left-1/2 top-0 mt-1 line-clamp-2 w-max max-w-[12.5rem] -translate-x-1/2 text-center text-[11px] leading-[1.2] [overflow-wrap:anywhere] [text-shadow:0_0_4px_#fff,0_1px_2px_#fff]',
              isOwn || isTracked
                ? 'font-bold text-neutral-950'
                : 'font-medium text-neutral-600',
            )}
            title={name}
          >
            {name}
          </p>
        ) : null}
      </div>
    </OverlayViewF>
  );
}
